import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { lang } from "src/language/Strings";
import Image from "src/components/Image";
import useFileMutation from "src/api/hooks/useFileMutation";
import { useParams } from "react-router-dom";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import { useFilesForCompanyId } from "src/api/hooks/useFiles";
import { Modal } from "src/components/Modal";
import { usePosition } from "src/api/hooks/usePosition";
import { WidgetProps } from "@rjsf/utils";
import { useFormSchema } from "src/api/hooks/useFormSchema";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import { useQueryClient } from "@tanstack/react-query";
import { portalApiClient } from "src/api/apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { queryKeys } from "src/api/queryKeys";
import { uploadFileToBlob } from "src/api/uploadFileToBlob";
import { ConditionalWidgetProps } from "./ConditionalWidgetProps";

// Custom hook for managing upload progress with throttling
function useUploadProgress() {
  const [progress, setProgress] = useState<Record<string, number>>({});
  const progressRef = useRef<Record<string, number>>({});
  const animationFrameRef = useRef<number | null>(null);
  const { deviationId, inspectionId } = useParams();
  const updateProgress = useCallback(
    (fileName: string, newProgress: number) => {
      // Store the latest progress
      progressRef.current[fileName] = newProgress;

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Schedule update for next frame
      animationFrameRef.current = requestAnimationFrame(() => {
        setProgress({ ...progressRef.current });
      });
    },
    []
  );

  const resetProgress = useCallback((fileName: string) => {
    setProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return { progress, updateProgress, resetProgress };
}

export default function ImageWidget(props: ConditionalWidgetProps) {
  const [files, setFiles] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeFile, setActiveFile] = useState<any | null>(null);
  const multipleInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>("");
  const { data: companyFiles } = useFilesForCompanyId();
  const pendingOnChangeRef = useRef<boolean>(false);

  const { companyId } = useParams();
  const {
    progress: uploadProgress,
    updateProgress,
    resetProgress,
  } = useUploadProgress();

  const handleUploadProgress = useCallback(
    (progress: number, fileName: string) => {
      updateProgress(fileName, Math.round(progress * 100));
    },
    [updateProgress]
  );
  const { create: createFile } = useFileMutation(handleUploadProgress);
  const position = usePosition();

  // Notify parent when files change (only when pendingOnChangeRef is set)
  useEffect(() => {
    if (pendingOnChangeRef.current && props.onChange) {
      props.onChange(files.map((f) => f.id));
      pendingOnChangeRef.current = false;
    }
  }, [files, props.onChange]);

  // Initialize files from props.value if available
  useEffect(() => {
    if (props.value && Array.isArray(props.value) && props.value.length > 0) {
      setFiles((prevFiles) => {
        // Only initialize if files array is empty or if props.value has changed significantly
        if (prevFiles.length === 0) {
          return props.value;
        }
        // If props.value has different files, update (handles external updates)
        const propsFileIds = new Set(
          props.value.map((f: any) => f.id || f.fileName).filter(Boolean)
        );
        const currentFileIds = new Set(
          prevFiles.map((f: any) => f.id || f.fileName).filter(Boolean)
        );
        if (
          propsFileIds.size !== currentFileIds.size ||
          ![...propsFileIds].every((id) => currentFileIds.has(id))
        ) {
          return props.value;
        }
        return prevFiles;
      });
    } else if (
      !props.value ||
      (Array.isArray(props.value) && props.value.length === 0)
    ) {
      // If props.value is cleared, clear local state too
      setFiles([]);
    }
  }, [props.value]);

  // Handle file input change
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        setActiveFile({
          file: file,
          companyId: Number(companyId),
          comment: "",
          depiction: "",
          schemaId: crypto.randomUUID() || undefined,
          fileType: FileType.Image,
          fileName: file.name,
          files: [],
          boundingBox: "",
          exif: "",
          position: {
            latitude: position.latitude || 0,
            longitude: position.longitude || 0,
            adress: "",
            geometry: `{"type":"Point","coordinates":[${
              position.longitude || 0
            },${position.latitude || 0}]}`,
          },
        });
        setError(""); // Clear any previous errors
      }
    },
    [companyId, position]
  );

  // Reset input when modal is closed (allows same file to be selected again)
  const handleModalClose = useCallback(() => {
    setActiveFile(null);
    setError("");
    // Reset input so the same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, []);

  function handleFileRemoval(filename: string) {
    const updatedFiles = files.filter((f) => f.file?.name !== filename);
    // Mark that we need to notify parent after state update
    pendingOnChangeRef.current = true;
    setFiles(updatedFiles);
    resetProgress(filename);

    const dataTransfer = new DataTransfer();
    const currentFiles = multipleInputRef.current?.files;

    if (currentFiles) {
      for (let i = 0; i < currentFiles.length; i++) {
        if (currentFiles[i].name !== filename) {
          dataTransfer.items.add(currentFiles[i]);
        }
      }
      if (multipleInputRef.current) {
        multipleInputRef.current.files = dataTransfer.files; // Update the FileList after removal
      }
    }
  }

  useEffect(() => {
    if (createFile.isSuccess && createFile.data) {
      const newFile = {
        file: createFile.data?.file ?? activeFile?.file,
        url: createFile.data?.files?.[0],
        fileName: createFile.data?.file?.name ?? activeFile?.fileName,
        companyId: Number(companyId),
        comment: createFile.data?.comment ?? activeFile?.comment,
        depiction: createFile.data?.depiction ?? activeFile?.depiction,
        id: createFile.data?.id,
        schemaId: createFile.data?.schemaId ?? activeFile?.schemaId,
        position: createFile.data?.position ?? activeFile?.position,
      };

      setFiles((prevFiles) => {
        // Check if file already exists
        if (
          prevFiles.some(
            (f) => f.fileName === newFile.fileName || f.id === newFile.id
          )
        ) {
          return prevFiles;
        }
        // Mark that we need to notify parent after state update
        pendingOnChangeRef.current = true;
        return [...prevFiles, newFile];
      });

      handleModalClose();
    } else if (createFile.isError) {
      setError(createFile.error?.message || "Failed to upload file");
      // Don't close modal on error, let user retry or cancel
      // Only reset input if user explicitly cancels
    }
  }, [
    createFile.isSuccess,
    createFile.isError,
    createFile.data,
    createFile.error,
    activeFile,
    companyId,
    handleModalClose,
  ]);

  return props.display ? (
    <>
      <ShowImages {...props} />
    </>
  ) : (
    <>
      <div
        className={`border-2 border-gray-500 rounded-lg p-2 h-40 grid grid-rows-4`}
      >
        <div className="w-full overflow-y-auto row-span-3">
          {files.map((f, index) => (
            <div
              key={index}
              className={`grid grid-cols-6 relative ${
                uploadProgress[f.fileName!] && uploadProgress[f.fileName!] < 100
                  ? "opacity-80"
                  : ""
              }`}
            >
              <div
                className={`absolute h-full transition-all duration-150 ease-out ${
                  uploadProgress[f.fileName!]
                    ? "opacity-50 bg-PrimaryGreen"
                    : ""
                }`}
                style={{
                  width: `${uploadProgress[f.fileName!] || 0}%`,
                }}
              >
                {" "}
              </div>
              <span className="ml-2 truncate col-span-3">{f.fileName}</span>
              <span className="flex col-span-2 items-center justify-center text-xs text-gray-600">
                {uploadProgress[f.fileName!]
                  ? `${Math.round(uploadProgress[f.fileName!])}%`
                  : ""}
              </span>
              <span className="flex col-span-1 items-center justify-end">
                <i
                  className="fa-solid fa-times text-lg ml-2 cursor-pointer mr-2"
                  onClick={() => handleFileRemoval(f.fileName!)}
                ></i>
              </span>
            </div>
          ))}
        </div>
        <input
          ref={inputRef}
          type="file"
          id="file-input"
          className="hidden"
          accept="image/*"
          required={!!props.required}
          onChange={handleFileChange}
        />

        <div>
          <button
            id={`${props.id}-file-input-button`}
            name={"file-input-button"}
            onClick={(e) => {
              e.preventDefault();
              if (inputRef.current) inputRef.current.click();
            }}
            className="border-2 rounded pt-1 pb-1 pl-2 pr-2"
          >
            {lang.addImage}
          </button>
        </div>
        <div className="h-4 w-full text-sm text-PrimaryRed pt-1">{error}</div>
      </div>
      {activeFile && (
        <ActiveFileModal
          activeFile={activeFile}
          setActiveFile={handleModalClose}
          companyFiles={companyFiles || []}
          onClick={createFile.mutate as any}
        />
      )}
    </>
  );
}

export function FileInputButton({ id }: { id: string }) {
  const { data: currentItem } = useProjectVariationById(id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { companyId } = useParams();
  const queryClient = useQueryClient();

  const [activeFile, setActiveFile] = useState<SasFileResult | null>(null);
  const { data: companyFiles } = useFilesForCompanyId();
  const position = usePosition();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setActiveFile({
        file: file,
        companyId: Number(companyId),
        comment: "",
        depiction: "",
        schemaId: crypto.randomUUID(), // Make this a guid
        fileType: FileType.Image,
        fileName: file.name,
        files: [],
        boundingBox: "",
        exif: "",
        position: {
          latitude: position.latitude || 0,
          longitude: position.longitude || 0,
          adress: currentItem?.title || "",
          geometry: `{"type":"Point","coordinates":[${
            position.longitude || 0
          },${position.latitude || 0}]}`,
        },
      });
    }
  };

  return (
    <div
      id={`${id}-file-input-div`}
      className="border-2 rounded pt-1 pb-1 pl-2 pr-2"
    >
      <input
        ref={fileInputRef}
        type="file"
        id={`${id}-file-input`}
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        id={`${id}-file-input-button`}
        name={"file-input-button"}
        onClick={() => {
          if (fileInputRef.current) fileInputRef.current.click();
        }}
      >
        {lang.addImage}
      </button>
      {activeFile && (
        <ActiveFileModal
          activeFile={activeFile}
          setActiveFile={setActiveFile}
          companyFiles={companyFiles || []}
          onClick={async (sasFile) => {
            const file = await portalApiClient.post<SasFileResult>(
              apiUrls.file.create.url(),
              sasFile
            );
            uploadFileToBlob({ ...sasFile, ...file.data }).then(() => {
              setActiveFile(null);
              queryClient.invalidateQueries({
                queryKey: queryKeys.selectedVariation(id),
              });
            });
          }}
        />
      )}
    </div>
  );
}

function ActiveFileModal({
  activeFile,
  setActiveFile,
  companyFiles,

  onClick,
}: {
  activeFile: any;
  setActiveFile: (file: any) => void;
  companyFiles: any[];
  onClick: (sasFile: SasFileResult) => void;
}) {
  const handleCancel = () => {
    setActiveFile(null);
  };

  return (
    <Modal onOutsideClick={handleCancel}>
      <Image filename={URL.createObjectURL(activeFile?.file!)} />

      <label htmlFor="type" className="text-lg text-PrimaryBlack">
        {lang.objectType}
      </label>
      <datalist id="object-type-list" className="hidden">
        {companyFiles
          ?.reduce((acc: string[], obj) => {
            if (obj.depiction && !acc.includes(obj.depiction)) {
              acc.push(obj.depiction);
            }
            return acc;
          }, [] as string[])
          .map((title, index) => (
            <option key={index} value={title} />
          ))}
      </datalist>
      <input
        id="type"
        type="text"
        name="objectType"
        list="object-type-list"
        autoComplete="off"
        placeholder={lang.objectType}
        className="border-2 border-Grey9 rounded-lg p-2"
      />
      <label htmlFor="comment" className="text-lg text-PrimaryBlack">
        {lang.description}
      </label>
      <input
        id="comment"
        type="text"
        placeholder={lang.description}
        className="border-2 border-Grey9 rounded-lg p-2"
        autoComplete="comment"
      />

      <div className="flex justify-center gap-2">
        <button
          className="bg-PrimaryBlue text-white rounded-lg p-2 border-2 bg-PrimaryGreen text-White"
          id="cancel-file-input-button"
          onClick={handleCancel}
        >
          {lang.cancel}
        </button>
        <button
          className="bg-PrimaryBlue text-white rounded-lg p-2 border-2 bg-PrimaryGreen text-White"
          id="create-file-input-button"
          onClick={() => {
            const sasFile = {
              ...activeFile,
              comment:
                (document.getElementById("comment") as HTMLInputElement)
                  ?.value || "",
              depiction:
                (document.getElementById("type") as HTMLInputElement)?.value ||
                "",
            } as SasFileResult;
            onClick(sasFile);
          }}
          type="button"
        >
          {lang.create}
        </button>
      </div>
    </Modal>
  );
}

function ShowImages(props: ConditionalWidgetProps) {
  const { data: files } = useFilesForCompanyId();
  const resultFiles = useMemo(() => {
    if (!files || !props.value) return [];
    return files?.filter((f) => props.value?.includes(f.id));
  }, [files, props.value]);
  if (!resultFiles || !props.display || resultFiles.length === 0) return null;

  return (
    <div className="flex flex-row gap-4 overflow-x-auto p-4 w-full">
      {resultFiles.map((f) => (
        <div
          key={f.id}
          className="flex-shrink-0 flex flex-col max-w-full overflow-x-auto gap-2 p-3 bg-White border border-Grey4 rounded-lg shadow-sm"
        >
          <Image
            key={f.id}
            filename={f.files[0]}
            thumbnail={f.webUrl!}
            imageClassName="max-h-52"
          />
          {(f.comment || f.depiction) && (
            <div className="flex flex-col gap-1 min-w-0">
              {f.depiction && (
                <div className="text-sm font-semibold  truncate">
                  <span className="secondary-text">{lang.type}: </span>
                  {f.depiction}
                </div>
              )}
              {f.comment && (
                <div className="text-sm  break-words">
                  <span className="secondary-text">{lang.description}: </span>
                  {f.comment}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
